"use client";

import HeaderSection from "~/components/ui/HeaderSection";
import Guest from "~/assets/guestbook/icon.svg";
import GuestIcon from "~/assets/icons/navstevy.svg";
import LinkArrow from "~/assets/icons/link-arrow.svg";
import React, { type Dispatch, type SetStateAction, useState } from "react";
import { Breakpoints, useView } from "~/hooks/useView";
import {QueryClientProvider, useInfiniteQuery} from "@tanstack/react-query";
import InViewWrapper from "~/components/layout/InViewWrapper";
import {
  getGuestbook,
  postGuestbookMessage,
  postGuestbookMessageReply,
} from "~/app/api/_backend/guestbook";
import { FetchLoading } from "~/components/ui/FetchLoading";
import { PiSmileySadLight } from "react-icons/pi";
import Icon from "~/components/ui/Icon";
import type { components } from "~/types/api-types";
import Moment from "moment";
import { AnimatePresence, motion } from "framer-motion";
import { Progress } from "~/components/ui/progress";
import FullscreenModal from "~/components/ui/FullscreenModal";
import MessageForm, {
  type MessageFormInput,
} from "./MessageForm";
import { FormProvider, type SubmitHandler, useForm } from "react-hook-form";
import getQueryClient from "~/lib/getQueryClient";

const queryClient = getQueryClient();

export default function Page() {
  const [messageModal, setMessageModal] = useState(false);
  const [message, setCurrentMessage] = useState<Comment | null>(null);
  const [submit, setSubmit] = useState(false);
  const [error, setError] = useState<boolean | null>(false);
  const [success, setSuccess] = useState(false);

  const methods = useForm<MessageFormInput>();
  const { formState, reset, handleSubmit } = methods;

  const onSubmit: SubmitHandler<MessageFormInput> = async (data) => {
    setSubmit(true);
    try {
      if (!message) {
        await postGuestbookMessage({
          name: data.name,
          subtitle: data.subtitle ?? "Bez předmětu",
          message: data.message,
        });
      } else {
        await postGuestbookMessageReply({
          name: data.name,
          message: data.message,
          id: message.id,
        });
      }
      setSuccess(true);
    } catch {
      setError(true);
    }
    setSubmit(false);
  };

  const openModal = async (b: boolean) => {
    setMessageModal(b);
    if (!b) {
      reset();
      if (success) {
        await queryClient.refetchQueries();
      }
      setSuccess(false);
      setError(null);
    }
  };

  return (
    <>
      <main className="container-section">
        <HeaderSection
          image={<Guest />}
          title="Návštěvy"
          text="Prostor s možností napsání vlastní zprávy, díky které je možné vyjádřit svůj pohled na různé druhy zmrzliny nebo obecný dojem ze zmrzliny 2AD."
        >
          <button
            className="btn btn-primary ripple"
            onClick={async () => {
              setCurrentMessage(null);
              await openModal(true);
            }}
          >
            Napsat zprávu
          </button>
        </HeaderSection>

        <QueryClientProvider client={queryClient}>
          <Comments setMessage={setCurrentMessage} setReplyModal={openModal} />
        </QueryClientProvider>

        <FullscreenModal
          open={messageModal}
          setOpenAction={openModal}
          isDirty={formState.isDirty && !success}
        >
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <MessageForm
                message={message}
                isSubmitted={submit}
                hasError={error ?? false}
                setErrorAction={setError}
                closeModalAction={async () => {
                  await openModal(false);
                }}
                success={success}
              />
            </form>
          </FormProvider>
        </FullscreenModal>
      </main>
    </>
  );
}

interface CommentProps {
  setReplyModal: (b: boolean) => void;
  setMessage: Dispatch<SetStateAction<Comment | null>>;
}

const Comments = ({ setReplyModal, setMessage }: CommentProps) => {
  const isMobileView = useView(Breakpoints.Mobile);
  const {
    isPending,
    isError,
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["guestbook"],
    queryFn: async ({ pageParam }) => {
      return await getGuestbook({
        page: pageParam,
        size: 5,
        sort: ["createdAt,desc"],
      });
    },
    initialPageParam: 0,
    getNextPageParam: ({ currentPage, totalPages }) => {
      const nextPage = currentPage + 1;
      if (nextPage > totalPages) return null;
      return nextPage;
    },
  });

  if (isPending) {
    return (
      <section className="m-auto mt-36 w-11/12 sm:w-10/12 xl:w-6/12">
        <FetchLoading />
      </section>
    );
  }

  if (isError) {
    return (
      <section className="m-auto mt-36 w-11/12 sm:w-10/12 xl:w-6/12">
        <div className="mb-3 text-3xl">
          <PiSmileySadLight />
        </div>
        Nepodařilo se načíst návštěvní knihu. Error {error.message}
      </section>
    );
  }

  return (
    <section className="m-auto mt-36 w-11/12 sm:w-10/12 xl:w-6/12">
      {data?.pages.map((messages, i) => (
        <React.Fragment key={i}>
          {messages.content.map((message, idx) => (
            <InViewWrapper
              key={idx}
              variant={{
                visible: {
                  opacity: 1,
                  transition: {
                    staggerChildren: isMobileView ? 0 : 0.2,
                  },
                },
                hidden: { opacity: 0 },
              }}
            >
              <CommentCard
                key={idx}
                comment={message}
                setReplyModal={setReplyModal}
                setMessage={setMessage}
              />
            </InViewWrapper>
          ))}
        </React.Fragment>
      ))}
      <div className="mt-24 flex flex-col items-center gap-4">
        <p className="text-primary">
          Zobrazeno {data?.pages.reduce((acc, c) => acc + c.content.length, 0)}{" "}
          z {data?.pages?.[0]?.totalElements}
        </p>
        <div className={"w-9/12 sm:w-6/12"}>
          <Progress
            value={
              (data?.pages.reduce((acc, c) => acc + c.content.length, 0) /
                (data?.pages?.[0]?.totalElements ?? 1)) *
              100
            }
          />
        </div>
        <button
          disabled={!hasNextPage || isFetching || isFetchingNextPage}
          onClick={() => fetchNextPage()}
          className="enabled:ripple btn btn-primary"
        >
          {isFetchingNextPage ? (
            <span>Načítávám</span>
          ) : (
            <span>Načíst další</span>
          )}
        </button>
      </div>
    </section>
  );
};

type Comment = components["schemas"]["GuestbookResponse"];

interface Props {
  comment: Comment;
  setReplyModal: (b: boolean) => void;
  setMessage: Dispatch<SetStateAction<Comment | null>>;
}

function CommentCard({ comment, setReplyModal, setMessage }: Props) {
  const hasReplies = comment.replies.length > 0;
  const [collapse, setCollapse] = useState(false);

  const reply = () => {
    setMessage(comment);
    setReplyModal(true);
  };

  return (
    <div className="mt-12 w-full rounded-[32px] bg-white p-[40px] shadow-[0_4px_16px_0_rgba(0,0,0,0.08)]">
      <Icon>
        <GuestIcon />
      </Icon>
      <div className="flex flex-col">
        <h3 className="h3 !font-[800] text-primary">{comment.name}</h3>
        <div className="mb-6 mt-4 text-[16px] font-[400] text-primary">
          {comment.subtitle} &#8226;{" "}
          {Moment(new Date(comment.createdAt)).format("DD. MM. YYYY")}
        </div>
        <p className="mb-10 whitespace-pre-line">
          {comment.message.replaceAll("\\n", "\n")}
        </p>
      </div>
      <button className="btn btn-secondary ripple" onClick={() => reply()}>
        Odpovědět
      </button>
      {hasReplies && (
        <button
          className="btn btn-secondary ripple ml-5"
          onClick={() => setCollapse(!collapse)}
        >
          Odpovědi
          <LinkArrow
            className={
              "arrow inline-block -rotate-90 lg:rotate-0 " +
              (!collapse ? "lg:!-rotate-180" : "")
            }
          />
        </button>
      )}
      <AnimatePresence initial={false}>
        {hasReplies && collapse && (
          <motion.div
            initial="collapsed"
            exit="collapsed"
            animate="open"
            variants={{
              open: { opacity: 1, height: "auto" },
              collapsed: { opacity: 0, height: 0 },
            }}
            transition={{
              duration: 0.2,
              ease: "linear",
              opacity: {
                duration: 0.19,
              },
            }}
          >
            {comment.replies.map((reply, idx) => {
              return (
                <div key={idx} className="mt-10">
                  <p className="text-primary">
                    <b className="font-[600]">{reply.name}</b> &#8226;{" "}
                    {comment.subtitle} &#8226;{" "}
                    {Moment(new Date(reply.createdAt)).format("DD. MM. YYYY")}
                  </p>
                  <p>{reply.message.replaceAll("\\n", "\n")}</p>
                </div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
